import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { ChevronDown, Search } from 'lucide-react';

const PAGE_SIZE = 30;

/**
 * Searchable, truncated version selector.
 * Replaces the Radix Select that was rendering 600+ items per dropdown.
 * Shows at most MAX_VISIBLE matching versions with a search input.
 */
function VersionSelect({ versions, value, onValueChange, placeholder = 'Select' }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  // Pre-process version data once when versions array changes
  const versionItems = useMemo(() => {
    return versions.map(doc => {
      const docId = doc.id;
      const data = doc.data();
      const owner = data.owner ? data.owner.split(' ')[0] : 'Unknown';
      const capitalisedName = owner.charAt(0).toUpperCase() + owner.slice(1);
      return {
        id: docId,
        numericId: parseInt(docId, 10),
        owner: capitalisedName,
        description: data.description || '',
        searchText: `${docId} ${capitalisedName} ${data.description || ''}`.toLowerCase(),
      };
    });
  }, [versions]);

  // All matches (before truncation)
  const allMatches = useMemo(() => {
    const term = search.toLowerCase().trim();
    return term
      ? versionItems.filter(v => v.searchText.includes(term))
      : versionItems;
  }, [versionItems, search]);

  // Truncate to visible count
  const filtered = useMemo(() => allMatches.slice(0, visibleCount), [allMatches, visibleCount]);
  const totalMatches = allMatches.length;

  // Reset visible count when search changes
  useEffect(() => { setVisibleCount(PAGE_SIZE); }, [search]);

  // Load more on scroll to bottom
  const handleListScroll = useCallback((e) => {
    const el = e.target;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 20) {
      setVisibleCount(prev => Math.min(prev + PAGE_SIZE, totalMatches));
    }
  }, [totalMatches]);

  // Resolve display label for current value
  const displayLabel = useMemo(() => {
    if (value === '' || value == null) return null;
    if (value === 'invalid') return `Invalid`;
    const item = versionItems.find(v => v.id === value.toString());
    if (item) return `${item.id} (${item.owner})`;
    return value.toString();
  }, [value, versionItems]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setSearch('');
        setVisibleCount(PAGE_SIZE);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Focus search input when opened
  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  const handleSelect = (id) => {
    onValueChange(id);
    setOpen(false);
    setSearch('');
    setVisibleCount(PAGE_SIZE);
  };

  return (
    <div ref={containerRef} className="relative">
      {/* Trigger button — matches the existing SelectTrigger styling */}
      <button
        type="button"
        onClick={() => setOpen(prev => !prev)}
        className="flex h-7 w-full items-center justify-between rounded-md border border-input bg-background px-2 py-1 text-xs ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
      >
        <span className={`truncate ${displayLabel ? '' : 'text-muted-foreground'}`}>
          {displayLabel || placeholder}
        </span>
        <ChevronDown className="h-3 w-3 opacity-50 flex-shrink-0 ml-1" />
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute z-50 mt-1 w-[320px] rounded-md border bg-popover text-popover-foreground shadow-md animate-in fade-in-0 zoom-in-95">
          {/* Search input */}
          <div className="flex items-center border-b px-2 py-1.5">
            <Search className="h-3 w-3 text-muted-foreground mr-1.5 flex-shrink-0" />
            <input
              ref={inputRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search versions..."
              className="flex-1 bg-transparent text-xs outline-none placeholder:text-muted-foreground"
            />
          </div>

          {/* Version list */}
          <div ref={listRef} onScroll={handleListScroll} className="max-h-[300px] overflow-y-auto p-1">
            {filtered.length === 0 ? (
              <div className="py-3 text-center text-xs text-muted-foreground">No versions found</div>
            ) : (
              filtered.map(item => {
                const isSelected = value != null && value.toString() === item.id;
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => handleSelect(item.id)}
                    className={`flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-xs outline-none hover:bg-accent hover:text-accent-foreground ${isSelected ? 'bg-accent' : ''}`}
                  >
                    <span className="font-medium whitespace-nowrap">{item.id} ({item.owner})</span>
                    {item.description && (
                      <span className="text-muted-foreground truncate">{item.description}</span>
                    )}
                  </button>
                );
              })
            )}
          </div>

          {/* Footer count */}
          {totalMatches > filtered.length && (
            <div className="border-t px-2 py-1 text-[10px] text-muted-foreground text-center">
              Showing {filtered.length} of {totalMatches} — scroll or type to see more
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default React.memo(VersionSelect);
